import { generatePopupConfig } from '@/features/Popup/helpers/generatePopupConfig';
import { POPUP_TYPES } from '@/features/Popup/constants';
import type { PopupTypes } from '@/features/Popup/types';
import { getJackpotModeParam } from '@/features/SWJ/helpers/resolveJackpotMode';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import {
  type DdAction,
  type DdError,
  DdEventCategory,
  DdEventType,
} from '@/utilities/logger/datadog/types';
import { getPlatform } from '@/utilities/getters/gameContextGetters';

type ShowSwjErrorPopup = (
  config: ReturnType<typeof generatePopupConfig>,
  type: typeof POPUP_TYPES.SWJ,
) => void;

/**
 * When the player opts out via `?jackpotMode=false`, swj-consumer MDS feed
 * timers may outlive TopBar unmount after a soft reload. Stable handlers read
 * the URL at invoke time so orphaned callbacks do not flood RUM or popups.
 */
export const isSwjConsumerErrorReportingMuted = (): boolean =>
  getJackpotModeParam() === 'false';

export const logSwjConsumerRumError = (
  name: string,
  payload: Omit<DdError, 'name'>,
): void => {
  if (isSwjConsumerErrorReportingMuted()) {
    return;
  }
  Logger.logRumError({ name, ...payload } as DdError);
};

export const logSwjConsumerRumAction = (
  name: string,
  payload: Omit<DdAction, 'name'>,
): void => {
  Logger.logRumAction({ name, ...payload } as DdAction);
};

export const reportSwjConsumerErrorPopup = (
  showPopup: ShowSwjErrorPopup,
  payload: {
    type: PopupTypes;
    data?: { errorCode?: string; errorMessage?: string };
  },
  gameUid: string | undefined,
): void => {
  if (isSwjConsumerErrorReportingMuted()) {
    return;
  }

  showPopup(
    generatePopupConfig(POPUP_TYPES.SWJ, {
      errorConfig: { errorCode: payload?.data?.errorCode },
    }),
    POPUP_TYPES.SWJ,
  );
  const reasons = [
    `${payload?.data?.errorCode} ${payload?.data?.errorMessage}`,
  ];
  Logger.logRumError({
    name: '[GW][SWJ]jackpots_jackpot_mode_error',
    useCase: DdEventCategory.GameContext,
    eventType: DdEventType.Error,
    context: {
      gameId: gameUid,
      platform: getPlatform(),
      SWJSuccessfullyLaunched: false,
      reasons,
      reasonsLength: reasons.length,
      failureReasonCode: payload?.data?.errorCode,
      timestamp: new Date().getTime(),
      logVersion: 2.0,
    },
  });
};
